/**
 * Pure parser for `{{…}}` template placeholders.
 *
 * Extracted from `template-decoration.ts` so the syntax linter (and any
 * other pure-logic consumer) can reuse the match data without pulling
 * in the decoration module, which imports `.css.ts` styles. Under the
 * plain vitest runner those stylesheet imports trip vanilla-extract's
 * file-scope guard and refuse to load.
 *
 * Grammar (relaxed):
 *   `{{` whitespace? IDENT ( `.` IDENT )* whitespace? `}}`
 *
 * where IDENT is `[A-Za-z0-9_-]+` (matches the `field-<uuid>-<name>`
 * format the runner uses). The runner's template resolver is still the
 * final authority on whether a placeholder is legal; this parser's
 * only job is to recognise the range for highlighting / diagnostic
 * suppression.
 */

export type TemplateTokenKind = 'variable' | 'credential' | 'env';

export interface TemplateTokenMatch {
  readonly kind: TemplateTokenKind;
  readonly from: number;
  readonly to: number;
  readonly text: string;
}

const TOKEN_REGEX = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;

export function parseTemplateTokens(input: string): TemplateTokenMatch[] {
  const matches: TemplateTokenMatch[] = [];
  TOKEN_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN_REGEX.exec(input)) !== null) {
    const [full, inner] = m;
    const first = inner.split('.')[0];
    let kind: TemplateTokenKind = 'variable';
    if (first === 'credential') kind = 'credential';
    else if (first === 'env') kind = 'env';
    matches.push({
      kind,
      from: m.index,
      to: m.index + full.length,
      text: full,
    });
  }
  return matches;
}
